import validUrl from 'valid-url';
import email from 'vuelidate/lib/validators/email';
import { pipe } from 'ramda';

function isString(val) {
  return typeof val === 'string' || val instanceof String
}

function isObject(val) {
  return typeof val === 'object' && !isArray(val) && val !== null
}

function isArray(val) {
  if (!val) return false

  return Array.isArray(val)
}

function valuesToLowercase(val) {
  if (isString(val)) {
    return val.toLowerCase()
  }

  if (isObject(val)) {
    return Object.entries(val).reduce((acc, [key, objVal]) => {
      return {
        ...acc,
        [key]: valuesToLowercase(objVal)
      }
    }, {})
  }

  if (isArray(val)) {
    return val.map((item) => valuesToLowercase(item))
  }

  return val
}

function getBinaryStringFromBooleanArray(arr) {
  return arr.reduce((acc, value) => acc + +!!value, '')
}

function checkIfFunction(val) {
  return val && (typeof val === 'function' || val instanceof Function)
}

/**
 * This function replace Object key names by another names
 * Implemented only for the 1st level (no deep implementation)
 * @param obj     - Object to remap
 * @param keyMap  - Object map where key - old key name, value - new key name
 * @returns { Object }
 */
function getRemapForNestedField(remap, parentKey) {
  const keyPart = `${parentKey}.`;
  return Object.entries(remap).reduce((acc, [key, value]) => {
    if (key.indexOf(keyPart) === 0) {
      return {
        ...acc,
        [key.split(keyPart)[1]]: value
      }
    }
    return acc
  }, {})
}

function remapObjectKeys(original, remap, modifier = (v) => v) {
  if (isArray(original)) {
    return original.map((v) => remapObjectKeys(v, remap, modifier))
  }

  if (isObject(original)) {
    return Object.entries(original).reduce((acc, [key, value]) => {
      const newValue =
        isObject(value) || isArray(value)
          ? remapObjectKeys(value, getRemapForNestedField(remap, key), modifier)
          : value;
      const newKey = remap[key] || modifier(key);
      return {
        ...acc,
        [newKey]: newValue
      }
    }, {})
  }

  return original
}

const hrefValidation = {
  handledCase: {
    isStartFromMailto: 'isStartFromMailto',
    isStartFromProtocol: 'isStartFromProtocol',
    isStartFromDoubleSlash: 'isStartFromDoubleSlash',
    isStartFromSingleSlash: 'isStartFromSingleSlash',
    isStartFromHash: 'isStartFromHash'
  },
  mockedProtocol: 'https',
  mockedDomain: 'domain.com',
  get regexpStringByCase() {
    return {
      [this.handledCase.isStartFromMailto]: '^mailto:.+',
      [this.handledCase.isStartFromProtocol]: '^[\\w\\-_]+:\\/\\/.+',
      [this.handledCase.isStartFromDoubleSlash]: '^//[^/]',
      [this.handledCase.isStartFromSingleSlash]: '^/[^/]',
      [this.handledCase.isStartFromHash]: '^#[^#]?'
    }
  },
  get handlerByCase() {
    return {
      [this.handledCase.isStartFromMailto]:
        this.validateIfStartsWithMailto.bind(this),
      [this.handledCase.isStartFromProtocol]:
        this.validateIfStartsWithProtocol.bind(this),
      [this.handledCase.isStartFromDoubleSlash]:
        this.validateIfStartsWithDoubleSlash.bind(this),
      [this.handledCase.isStartFromSingleSlash]:
        this.validateIfStartsWithSingleSlash.bind(this),
      [this.handledCase.isStartFromHash]:
        this.validateIfStartsWithHash.bind(this)
    }
  },
  validateIfStartsWithMailto(href) {
    const emailAddress = href.match(/mailto:(.+)/i)[1];

    return email(emailAddress)
  },
  validateIfStartsWithProtocol(href) {
    return !!validUrl.isUri(href)
  },
  validateIfStartsWithDoubleSlash(href) {
    const urlWithMockedProtocol = href.replace(
      /^\/\//i,
      `${this.mockedProtocol}://`
    );

    return this.validateIfStartsWithProtocol(urlWithMockedProtocol)
  },
  validateIfStartsWithSingleSlash(href) {
    const urlWithMockedProtocol = href.replace(
      /^\//i,
      `${this.mockedProtocol}://`
    );

    return this.validateIfStartsWithProtocol(urlWithMockedProtocol)
  },
  validateIfStartsWithHash(href) {
    const urlWithMockedPath = `${this.mockedProtocol}://${this.mockedDomain}${href}`;

    return this.validateIfStartsWithProtocol(urlWithMockedPath)
  },
  isHrefValid(href) {
    if (!href) return false

    const hrefCase = Object.values(this.handledCase).find((handledCase) => {
      const caseRegexp = new RegExp(this.regexpStringByCase[handledCase], 'si');

      return href.match(caseRegexp)
    });

    if (!hrefCase) return false

    const handler = this.handlerByCase[hrefCase];

    try {
      return handler(href)
    } catch (err) {
      return false
    }
  }
};

function isHrefValid(href) {
  return hrefValidation.isHrefValid(href)
}

function generateHandlersBySettings(handlersSettings, element) {
  return handlersSettings
    .filter(({ conditionFn }) => conditionFn(element))
    .map(({ handlerFn }) => handlerFn)
}

/**
 * This function returns another function which is used to handle parsed HTML
 * elements according to the handlersSettings
 *
 * @param {Object[]}    handlersSettings  - Array of handler settings which should be applied to parsed elements
 * @param {Function}    handlersSettings[].conditionFn  - function which receives parsed element and return TRUE if the element should be processed by the handler
 * @param {Function}    handlersSettings[].handlerFn    - handler which modifies parsed element according to the business logic
 *
 * @returns {Function}  Handler for parsed elements which contains all handlers according to the provided settings
 */
function generateParsedDomElementsHandlerDeep(handlersSettings) {
  return function processElements(elements) {
    if (!elements) return elements

    if (Array.isArray(elements)) {
      /**
       * Notes:
       * - We perform flat() because processElements may return either handled element
       * or array of elements (i.e. dewrap the tag - we return array of it's content)
       * - We perform filter() because processElements may return null after handling the
       * element (i.e. remove some tag)
       *
       * Summary: element handler receives an element as an input and may return:
       * - null
       * - element
       * - array of elements
       * Therefore we can create very flexible handlers.
       */
      return elements
        .map(processElements)
        .flat()
        .filter((el) => !!el)
    }

    const handlers = generateHandlersBySettings(handlersSettings, elements);
    const updatedEl = handlers.length ? pipe(...handlers)(elements) : elements;

    if (!updatedEl) return updatedEl

    if (Array.isArray(updatedEl)) {
      return processElements(updatedEl)
    }

    if (isObject(updatedEl)) {
      return {
        ...updatedEl,
        content: processElements(updatedEl.content)
      }
    }

    return updatedEl
  }
}

/**
 * Used to find if some Object/Array (not primitive) has some parent in the provided
 * data structure (checked by function)
 *
 * @param dataStructure     - any data structure with Objects, Arrays or primitives
 * @param targetElement     - Object or Array (not primitive)
 * @param parentConditionFn - function to check parent item for condition
 * @returns {boolean|*}     - returns true if targetElement has at least one parent item
 *                            which matches the condition
 */
function hasParentInDataStructureDeep({
  dataStructure,
  targetElement,
  parentConditionFn
}) {
  if (!targetElement || !parentConditionFn) return false

  function hasEl(elements, elToFind) {
    if (!elements) {
      return { isTargetElementFound: false, parentCondition: false }
    }

    if (Array.isArray(elements)) {
      return elements.reduce(
        (acc, element) => {
          if (acc.isTargetElementFound && acc.parentCondition) return acc

          const { isTargetElementFound, parentCondition } = hasEl(
            element,
            elToFind
          );
          if (isTargetElementFound && parentCondition) {
            return { isTargetElementFound, parentCondition }
          }
          if (isTargetElementFound) {
            return {
              isTargetElementFound,
              parentCondition: parentConditionFn(element)
            }
          }
          return acc
        },
        { isTargetElementFound: false, parentCondition: false }
      )
    }

    if (isObject(elements)) {
      if (elements === elToFind) {
        return { isTargetElementFound: true, parentCondition: false }
      }

      if (elements.content) {
        return hasEl(elements.content, elToFind)
      }

      return { isTargetElementFound: false, parentCondition: false }
    }

    return { isTargetElementFound: false, parentCondition: false }
  }

  const { isTargetElementFound, parentCondition } = hasEl(
    dataStructure,
    targetElement
  );

  if (!isTargetElementFound) {
    console.log(
      'hasParentInDataStructureDeep():\nElement was not found in the provided data structure'
    );
    return false
  }

  return parentCondition
}

export { checkIfFunction, generateParsedDomElementsHandlerDeep, getBinaryStringFromBooleanArray, hasParentInDataStructureDeep, isArray, isHrefValid, isObject, isString, remapObjectKeys, valuesToLowercase };
